import apiClient from "../api/axios";
import { CodeMap, handleApiError } from "../api/handleApiError";
import { Site, SubOrganizations } from "../models/SubOrganization";
import { StructureDeletePreview } from "../models/StructureDelete";
import { organizationText } from "./assets.service";

const SubOrganizationsService = {
  editSubOrganizations: async (
    formData: any,
  ): Promise<SubOrganizations[] | null> => {
    try {
      const response = await apiClient.post("/v1/suborganizations", formData);

      if (response.status === 200) {
        const result: SubOrganizations[] =
          response.data.results.suborganizations;
        return result;
      }
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  editSite: async (formData: any): Promise<Site | null> => {
    try {
      const response = await apiClient.post("/v1/sites", formData);

      if (response.status === 200) {
        const result: any = response.data.results.site;

        return {
          refId: result._id,
          id: result.id,
          name: result.name,
          nameEn: result.nameEn,
          nameTh: result.nameTh,
          location: result.location,
        };
      }
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  deleteSite: async (refId: string): Promise<boolean> => {
    try {
      const response = await apiClient.patch(`/v1/sites/${refId}/soft-delete`);

      if (response.status === 200) {
        return true;
      }
    } catch (error: any) {
      handleError(error);
    }
    return false;
  },

  editSubOrganizationsV2: async (
    formData: any,
    refId: string,
  ): Promise<SubOrganizations[] | null> => {
    try {
      const response = await apiClient.put(
        `/v2/suborganizations/${refId}`,
        formData,
      );

      if (response.status === 200) {
        const result: SubOrganizations[] =
          response.data.results.suborganizations;
        return result;
      }
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  addSuborganizationsV2: async (
    formData: any,
  ): Promise<SubOrganizations[] | null> => {
    try {
      const response = await apiClient.post("/v2/suborganizations", formData);

      if (response.status === 201) {
        const result: SubOrganizations[] =
          response.data.results.suborganizations;
        return result;
      }
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  addSiteV2: async (formData: any): Promise<Site | null> => {
    try {
      const response = await apiClient.post(`/v2/sites`, formData);
      const site = response.data?.results?.site;

      if (!site) return null;

      return {
        refId: site.refId,
        id: site.id,
        name: site.name,
        nameEn: site.nameEn,
        nameTh: site.nameTh,
        subdistrictRef: site.subdistrictRef,
        location: {
          address: site.location?.address ?? null,
          subdistrict: site.location?.subdistrict ?? null,
          district: site.location?.district ?? null,
          city: site.location?.city ?? null,
          country: site.location?.country ?? null,
          postalCode: site.location?.postalCode ?? null,
        },
      } as Site;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  editSiteV2: async (refId: string, formData: any): Promise<Site | null> => {
    try {
      const response = await apiClient.put(`/v2/sites/${refId}`, formData);
      const site = response.data?.results?.site;

      if (!site) return null;

      return {
        refId: site.refId,
        id: site.id,
        name: site.name,
        subdistrictRef: site.subdistrictRef,
        location: {
          address: site.location?.address ?? null,
          subdistrict: site.location?.subdistrict ?? null,
          district: site.location?.district ?? null,
          city: site.location?.city ?? null,
          country: site.location?.country ?? null,
          postalCode: site.location?.postalCode ?? null,
        },
      } as Site;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  getSiteDeletePreview: async (
    refId: string,
  ): Promise<StructureDeletePreview | null> => {
    try {
      const response = await apiClient.get(`/v2/sites/${refId}/delete-preview`);
      if (response.status === 200) {
        return response.data.results;
      }
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  deleteSiteV2: async (refId: string, fingerprint?: string): Promise<boolean> => {
    try {
      const response = await apiClient.patch(
        `/v2/sites/${refId}/soft-delete`,
        fingerprint ? { fingerprint } : {},
        { _own503Codes: ["EVIDENCE_DELETE_PROCESS_FAILED"] } as any,
      );
      if (response.status === 200) {
        return true;
      }
    } catch (error) {
      // creates answer 429 on their own lock too, so only the site delete reads it as another delete running
      handleError(error, {
        TOO_MANY_REQUESTS: { messageKey: organizationText("Structure-Delete-Busy"), type: "info" },
      });
    }
    return false;
  },
};

const handleError = (error: unknown, callCodes: CodeMap = {}) =>
  handleApiError(
    error,
    {
      SITE_ALREADY_EXISTS: "SITE_ALREADY_EXISTS",
      SITE_LAST_IN_SUBORGANIZATION: { messageKey: organizationText("Delete-Site-Unsuccess"), type: "info" },
      DELETE_PREVIEW_OUTDATED: { messageKey: organizationText("Structure-Delete-Outdated"), type: "info" },
      EVIDENCE_DELETE_PROCESS_FAILED: { messageKey: organizationText("Structure-Delete-Files-Retry"), type: "info" },
      ...callCodes,
    },
    "Cannot-Access-Organization-Error",
  );

export default SubOrganizationsService;
